"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useEffect, forwardRef, useContext } from "react";
import Label from "@ingka/label";
import Button from "@ingka/button";
import cx from "classnames";
import visibilityHide from "@ingka/ssr-icon/paths/visibility-hide";
import visibilityShow from "@ingka/ssr-icon/paths/visibility-show";
import SSRIcon from "@ingka/ssr-icon";
import { FormFieldContext } from "@ingka/form-field";
import ValidationBorder from "@ingka/validation-border";
import PaymentLogo from "@ingka/payment-logo";
const InputField = forwardRef(
  (props, ref) => {
    const {
      prefix = "",
      type = "text",
      label,
      placeholder,
      req = false,
      inputMode,
      disabled = false,
      readOnly = false,
      hidePasswordText = "Hide password",
      showPasswordText = "Show password",
      className,
      prefixLabel,
      suffixLabel,
      id,
      describedById,
      ssrIcon,
      iconPosition,
      iconOnClick,
      iconClickDescription,
      onChange,
      paymentLogoProps,
      announceAffix,
      ...other
    } = props;
    const { shouldValidate, valid, setCharacterCount } = useContext(FormFieldContext);
    const togglePasswordVisibility = (e) => {
      e.preventDefault();
      setPasswordVisible(!passwordVisible);
    };
    const onChangeHandler = (e) => {
      if (setCharacterCount) {
        setCharacterCount(e.target.value.length);
      }
      setLabelled(e.target.value !== "");
      if (onChange) {
        onChange(e);
      }
    };
    const { value, defaultValue } = other;
    let defaultCharacterCount = 0;
    let defaultLabelled = false;
    if (value || value === 0) {
      defaultCharacterCount = String(value).length;
      defaultLabelled = value === 0 || Boolean(value);
    } else if (defaultValue || defaultValue === 0) {
      defaultLabelled = defaultValue === 0 || Boolean(defaultValue);
      defaultCharacterCount = String(defaultValue).length;
    }
    const [labelled, setLabelled] = useState(defaultLabelled);
    const [passwordVisible, setPasswordVisible] = useState(false);
    useEffect(() => {
      setLabelled(defaultLabelled);
    }, [defaultLabelled]);
    useEffect(() => {
      if (setCharacterCount) {
        setCharacterCount(defaultCharacterCount);
      }
    }, [defaultCharacterCount]);
    const trailing = type === "password" || iconPosition === "trailing" && ssrIcon;
    const baseClass = `${prefix}input-field`;
    const classes = cx(
      `${baseClass}`,
      disabled && `${baseClass}--disabled`,
      readOnly && `${baseClass}--read-only`,
      prefixLabel && `${baseClass}--prefixed`,
      suffixLabel && `${baseClass}--suffixed`,
      ssrIcon && iconPosition === "leading" && `${baseClass}--leading-icon`,
      trailing && `${baseClass}--trailing-icon`,
      labelled && `${baseClass}--labelled`,
      `${prefix}label-wrapper ${prefix}label-wrapper--text-input`,
      className
    );
    const ariaInvalid = valid === "error";
    const convertedType = type === "number" ? "text" : type;
    const prefixLabelId = prefixLabel && announceAffix ? `${id}-prefix` : void 0;
    const suffixLabelId = suffixLabel && announceAffix ? `${id}-suffix` : void 0;
    const ariaDescribedByValue = [prefixLabelId, suffixLabelId, describedById].filter(Boolean).join(" ");
    const ariaDescribedBy = ariaDescribedByValue || null;
    return /* @__PURE__ */ jsxs("div", { className: classes, children: [
      label && /* @__PURE__ */ jsx(Label, { prefix, htmlFor: id, label }),
      /* @__PURE__ */ jsxs("div", { className: `${baseClass}__wrapper`, children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            type: passwordVisible ? "text" : convertedType,
            id,
            disabled,
            readOnly,
            required: req,
            ...other,
            onChange: onChangeHandler,
            inputMode: type === "number" ? "numeric" : inputMode,
            "aria-describedby": ariaDescribedBy,
            "aria-required": Boolean(req),
            "aria-invalid": shouldValidate ? ariaInvalid : void 0
          }
        ),
        iconPosition === "leading" && ssrIcon && /* @__PURE__ */ jsx(
          SSRIcon,
          {
            prefix,
            paths: ssrIcon,
            className: `${baseClass}__leading-icon`
          }
        ),
        prefixLabel && /* @__PURE__ */ jsx("span", { id: prefixLabelId, className: `${baseClass}__prefix-label`, children: prefixLabel }),
        suffixLabel && /* @__PURE__ */ jsx("span", { id: suffixLabelId, className: `${baseClass}__suffix-label`, children: suffixLabel }),
        type === "password" && /* @__PURE__ */ jsx(
          Button,
          {
            htmlType: "button",
            prefix,
            className: `${baseClass}__trailing-icon`,
            iconOnly: true,
            size: "xsmall",
            ssrIcon: passwordVisible ? visibilityHide : visibilityShow,
            onClick: togglePasswordVisibility,
            type: "tertiary",
            text: passwordVisible ? hidePasswordText : showPasswordText,
            disabled
          }
        ),
        !paymentLogoProps && iconPosition === "trailing" && ssrIcon ? /* @__PURE__ */ jsx(
          Button,
          {
            htmlType: "button",
            prefix,
            className: `${baseClass}__trailing-icon`,
            size: "xsmall",
            ssrIcon,
            iconOnly: true,
            onClick: iconOnClick ? iconOnClick : void 0,
            type: "tertiary",
            text: iconClickDescription,
            disabled
          }
        ) : null,
        paymentLogoProps && type !== "password" && /* @__PURE__ */ jsx(
          PaymentLogo,
          {
            ...paymentLogoProps,
            size: "small",
            className: `${baseClass}__payment-logo`
          }
        ),
        /* @__PURE__ */ jsx(
          ValidationBorder,
          {
            prefix,
            shouldValidate,
            valid,
            disabled,
            readOnly
          }
        )
      ] })
    ] });
  }
);
InputField.displayName = "InputField";
var InputField_default = InputField;
export {
  InputField_default as default
};
